import { createAction } from "@reduxjs/toolkit";
import { getIn } from "icepick";
import { denormalize, normalize, schema } from "normalizr";
import { t } from "ttag";
import _ from "underscore";

import {
  automagicDashboardsApi,
  cardApi,
  dashboardApi,
  embedApi,
  publicApi,
} from "metabase/api";
import { isAbortError } from "metabase/api/client";
import { runRtkEndpoint } from "metabase/api/utils/run-rtk-endpoint";
import { applyParameters } from "metabase/common/utils/card";
import { showAutoApplyFiltersToast } from "metabase/dashboard/actions/parameters";
import { DASHBOARD_SLOW_TIMEOUT } from "metabase/dashboard/constants";
import {
  getCanShowAutoApplyFiltersToast,
  getDashCardBeforeEditing,
  getDashCardById,
  getDashboardById,
  getDashboardComplete,
  getLinkTargetEntities,
  getLoadingDashCards,
  getParameterValues,
  getSelectedTabId,
} from "metabase/dashboard/selectors";
import {
  expandInlineDashboard,
  fetchDataOrError,
  getAllDashboardCards,
  getCurrentTabDashboardCards,
} from "metabase/dashboard/utils";
import { getMetadata, paramFieldsFetched } from "metabase/metadata-store";
import { getSavedDashboardUiParameters } from "metabase/parameters/utils/dashboards";
import { getParameterValuesByIdFromQueryParams } from "metabase/parameters/utils/parameter-parsing";
import { makePivotAwareQueryRunner } from "metabase/querying/api/query-endpoints";
import { runAdhocDatasetQuery } from "metabase/querying/run-query";
import type {
  DashboardLinkTargets,
  Dispatch,
  GetState,
} from "metabase/redux/store";
import { createAsyncThunk, createThunkAction } from "metabase/redux/utils";
import {
  getDashboardType,
  isQuestionDashCard,
  isVirtualDashCard,
} from "metabase/utils/dashboard";
import { uuid } from "metabase/utils/uuid";
import { getParameterValuesBySlug } from "metabase-lib/v1/parameters/utils/parameter-values";
import type {
  Card,
  CardId,
  DashCardId,
  Dashboard,
  DashboardCard,
  DashboardId,
  DashboardQueryMetadata,
  Dataset,
  JsonQuery,
  ParameterId,
  ParameterValuesMap,
  QuestionDashboardCard,
} from "metabase-types/api";
import { isVisualizerDashboardCard } from "metabase-types/guards/dashboard";

export const FETCH_DASHBOARD_CARD_DATA =
  "metabase/dashboard/FETCH_DASHBOARD_CARD_DATA";
export const fetchDashboardCardDataAction = createAction<{
  currentTime: number;
  loadingIds: DashCardId[];
}>(FETCH_DASHBOARD_CARD_DATA);

export const CANCEL_FETCH_DASHBOARD_CARD_DATA =
  "metabase/dashboard/CANCEL_FETCH_DASHBOARD_CARD_DATA";

export const FETCH_CARD_DATA = "metabase/dashboard/FETCH_CARD_DATA";
export const ADD_DASHCARD_IDS_TO_LOADING_QUEUE =
  "metabase/dashboard/ADD_DASHCARD_IDS_TO_LOADING_QUEUE";
export const addDashcardIdsToLoadingQueue = createAction(
  ADD_DASHCARD_IDS_TO_LOADING_QUEUE,
  (dashcard_id, card_id) => ({
    payload: {
      dashcard_id,
      card_id,
    },
  }),
);

export const CANCEL_FETCH_CARD_DATA =
  "metabase/dashboard/CANCEL_FETCH_CARD_DATA";

export const CLEAR_CARD_DATA = "metabase/dashboard/CLEAR_CARD_DATA";

export const SET_LOADING_DASHCARDS_COMPLETE =
  "metabase/dashboard/SET_LOADING_DASHCARDS_COMPLETE";

// real dashcard ids are integers >= 1
function isNewDashcard(dashcard: DashboardCard) {
  return dashcard.id < 0;
}

function isNewAdditionalSeriesCard(
  card: Card,
  dashcard: QuestionDashboardCard,
  dashcardBeforeEditing?: DashboardCard,
) {
  if (isVisualizerDashboardCard(dashcard)) {
    if (!dashcardBeforeEditing || !("series" in dashcardBeforeEditing)) {
      return false;
    }

    const prevSeries = dashcardBeforeEditing.series ?? [];
    const newSeries = dashcard.series ?? [];

    return (
      card.id !== dashcard.card_id &&
      !prevSeries.some((s) => s.id === card.id) &&
      newSeries.some((s) => s.id === card.id)
    );
  }

  return (
    card.id !== dashcard.card_id &&
    !dashcard.series?.some((s) => s.id === card.id)
  );
}

export const SET_DOCUMENT_TITLE = "metabase/dashboard/SET_DOCUMENT_TITLE";
export const setDocumentTitle = createAction<string>(SET_DOCUMENT_TITLE);

const updateLoadingTitle = createThunkAction(
  SET_DOCUMENT_TITLE,
  (totalCards) => (_dispatch, getState) => {
    const loadingDashCards = getLoadingDashCards(getState());
    const loadingComplete = totalCards - loadingDashCards.loadingIds.length;
    return `${loadingComplete}/${totalCards} loaded`;
  },
);

export const SET_SHOW_LOADING_COMPLETE_FAVICON =
  "metabase/dashboard/SET_SHOW_LOADING_COMPLETE_FAVICON";
export const setShowLoadingCompleteFavicon = createAction<boolean>(
  SET_SHOW_LOADING_COMPLETE_FAVICON,
);

const loadingComplete = createThunkAction(
  SET_LOADING_DASHCARDS_COMPLETE,
  () => (dispatch, getState) => {
    dispatch(setShowLoadingCompleteFavicon(true));

    if (!document.hidden) {
      dispatch(setDocumentTitle(""));
      setTimeout(() => {
        dispatch(setShowLoadingCompleteFavicon(false));
      }, 3000);
    } else {
      dispatch(setDocumentTitle(t`Your dashboard is ready`));
      document.addEventListener(
        "visibilitychange",
        () => {
          dispatch(setDocumentTitle(""));
          setTimeout(() => {
            dispatch(setShowLoadingCompleteFavicon(false));
          }, 3000);
        },
        { once: true },
      );
    }

    if (getCanShowAutoApplyFiltersToast(getState())) {
      dispatch(showAutoApplyFiltersToast());
    }
  },
);

export const MARK_CARD_AS_SLOW = "metabase/dashboard/MARK_CARD_AS_SLOW";
export const markCardAsSlow = createAction(MARK_CARD_AS_SLOW, (card: Card) => {
  return {
    payload: {
      id: card.id,
      result: true,
    },
  };
});

type FetchCardDataActionArgs = {
  card: Card;
  dashcard: DashboardCard;
  options: {
    clearCache?: boolean;
    ignoreCache?: boolean;
    reload?: boolean;
    dashboardLoadId?: string;
  };
};

type FetchCardDataActionReturned =
  | {
      dashcard_id: DashCardId;
      card_id: CardId;
      result: Dataset | { error: unknown } | null;
      currentTime?: number;
    }
  | undefined;

export const fetchCardDataAction = createAsyncThunk<
  FetchCardDataActionReturned,
  FetchCardDataActionArgs
>(
  FETCH_CARD_DATA,
  async (
    {
      card,
      dashcard,
      options: { reload, clearCache, ignoreCache, dashboardLoadId } = {},
    },
    { dispatch, getState },
  ) => {
    dispatch(addDashcardIdsToLoadingQueue(dashcard.id, card.id));

    // If the dataset_query was filtered then we don't have permission to view this card, so
    // shortcircuit and return a fake 403
    if (!card.dataset_query) {
      return {
        dashcard_id: dashcard.id,
        card_id: card.id,
        result: { error: { status: 403 } },
      };
    }

    const dashboardType = getDashboardType(dashcard.dashboard_id);

    const {
      dashboardId,
      dashboards,
      editingDashboard,
      parameterValues,
      dashcardData,
    } = getState().dashboard;

    if (!dashboardId) {
      return;
    }

    const dashboard = dashboards[dashboardId];

    // if the dashboard is being edited, ignore parameters that do not exist in
    // the saved dashboard to avoid query errors
    const savedParameterIds = new Set(
      editingDashboard?.parameters?.map((parameter) => parameter.id),
    );
    const savedParameters =
      editingDashboard != null
        ? dashboard.parameters?.filter((parameter) =>
            savedParameterIds.has(parameter.id),
          )
        : dashboard.parameters;

    // if we have a parameter, apply it to the card query before we execute
    const datasetQuery = applyParameters(
      card,
      savedParameters,
      parameterValues,
      dashcard?.parameter_mappings ?? undefined,
    );

    const lastResult = getIn(dashcardData, [dashcard.id, card.id]);
    if (!reload) {
      // if reload not set, check to see if the last result has the same query dict and return that
      if (
        lastResult &&
        _.isEqual(
          getDatasetQueryParams(lastResult.json_query),
          getDatasetQueryParams(datasetQuery),
        )
      ) {
        return {
          dashcard_id: dashcard.id,
          card_id: card.id,
          result: lastResult,
        };
      }

      /**
       * If a request for this card is already in-flight with the same parameters, let it finish rather than cancelling
       * and restarting. This avoids re-executing slow queries (e.g. pivot tables) when switching dashboard tabs back
       * and forth (#70534). When parameters differ (e.g. filter change), we fall through to the cancel-and-restart
       * path below.
       */
      const inFlight = cardDataCancelControllers[`${dashcard.id},${card.id}`];
      if (
        inFlight &&
        _.isEqual(inFlight.queryParams, getDatasetQueryParams(datasetQuery))
      ) {
        return;
      }
    }

    cancelFetchCardData(card.id, dashcard.id);

    // When dashcard parameters change, we need to clean previous (stale)
    // state so that the loader spinner shows as expected (#33767)
    const hasParametersChanged =
      !lastResult ||
      !_.isEqual(
        getDatasetQueryParams(lastResult.json_query),
        getDatasetQueryParams(datasetQuery),
      );

    if (clearCache || hasParametersChanged) {
      // clears the card data to indicate the card is reloading
      dispatch(clearCardData(card.id, dashcard.id));
    }

    let result: Dataset | { error: unknown } | null = null;

    // start a timer that will show the expected card duration if the query takes too long
    const slowCardTimer = setTimeout(() => {
      if (result === null) {
        dispatch(markCardAsSlow(card));
      }
    }, DASHBOARD_SLOW_TIMEOUT);

    const controller = new AbortController();
    setFetchCardDataCancel(
      card.id,
      dashcard.id,
      controller,
      getDatasetQueryParams(datasetQuery),
    );

    const metadata = getMetadata(getState());
    const runQuery = makePivotAwareQueryRunner(dispatch, controller.signal);

    if (dashboardType === "public") {
      // Unjustified type cast. FIXME
      result = (await fetchDataOrError(
        runQuery(publicApi.endpoints.getPublicDashcardQuery, card, metadata, {
          // In public dashboards `dashboard_id` holds the public UUID string.
          uuid: dashcard.dashboard_id as string,
          dashcardId: dashcard.id,
          cardId: card.id,
          parameters: datasetQuery.parameters
            ? JSON.stringify(datasetQuery.parameters)
            : undefined,
          ignore_cache: ignoreCache,
        }),
      )) as Dataset | { error: unknown };
    } else if (dashboardType === "embed") {
      // Unjustified type cast. FIXME
      result = (await fetchDataOrError(
        runQuery(embedApi.endpoints.getEmbedDashcardQuery, card, metadata, {
          // In embedded dashboards `dashboard_id` holds the embed token string.
          token: dashcard.dashboard_id as string,
          dashcardId: dashcard.id,
          cardId: card.id,
          parameters: JSON.stringify(
            getParameterValuesBySlug(dashboard.parameters, parameterValues),
          ),
          ignore_cache: ignoreCache,
        }),
      )) as Dataset | { error: unknown };
    } else if (dashboardType === "transient" || dashboardType === "inline") {
      // Unjustified type cast. FIXME
      result = (await fetchDataOrError(
        runAdhocDatasetQuery(
          dispatch,
          card,
          metadata,
          { ...datasetQuery, ignore_cache: ignoreCache },
          controller.signal,
        ),
      )) as Dataset | { error: unknown };
    } else {
      const dashcardBeforeEditing = getDashCardBeforeEditing(
        getState(),
        dashcard.id,
      );
      const hasReplacedCard =
        dashcard.card_id != null &&
        dashcardBeforeEditing &&
        dashcardBeforeEditing.card_id !== dashcard.card_id;

      const shouldUseCardQueryEndpoint =
        isNewDashcard(dashcard) ||
        (isQuestionDashCard(dashcard) &&
          isNewAdditionalSeriesCard(card, dashcard, dashcardBeforeEditing)) ||
        hasReplacedCard;

      // new dashcards and new additional series cards aren't yet saved to the dashboard, so they need to be run using the card query endpoint
      if (shouldUseCardQueryEndpoint) {
        // Unjustified type cast. FIXME
        result = (await fetchDataOrError(
          runQuery(cardApi.endpoints.getCardQuery, card, metadata, {
            cardId: card.id,
            dashboardId: dashcard.dashboard_id,
            ignore_cache: ignoreCache,
          }),
        )) as Dataset | { error: unknown };
      } else {
        // Unjustified type cast. FIXME
        result = (await fetchDataOrError(
          runQuery(
            dashboardApi.endpoints.getDashboardCardQuery,
            card,
            metadata,
            {
              dashboardId: dashcard.dashboard_id,
              dashcardId: dashcard.id,
              cardId: card.id,
              parameters: datasetQuery.parameters,
              ignore_cache: ignoreCache,
              dashboard_id: dashcard.dashboard_id,
              dashboard_load_id: dashboardLoadId,
            },
          ),
        )) as Dataset | { error: unknown };
      }
    }

    // If the request was not previously cancelled, then clear the controller for the card
    if (!controller.signal.aborted) {
      setFetchCardDataCancel(card.id, dashcard.id, null);
    }
    clearTimeout(slowCardTimer);

    return {
      dashcard_id: dashcard.id,
      card_id: card.id,
      result: controller.signal.aborted ? null : result,
      currentTime: performance.now(),
    };
  },
);

export const fetchCardData =
  (
    card: FetchCardDataActionArgs["card"],
    dashcard: FetchCardDataActionArgs["dashcard"],
    options: FetchCardDataActionArgs["options"] = {},
  ) =>
  async (dispatch: Dispatch) => {
    await dispatch(
      fetchCardDataAction({
        card,
        dashcard,
        options,
      }),
    );
  };

// Leave 1 connection free for interactive requests (browser HTTP/1.1 limit is 6)
const HTTP1_CONCURRENT_CARD_FETCH_LIMIT = 5;

function getCardsFetchingConcurrencyLimit(): number {
  try {
    // Unjustified type cast. FIXME
    const [navigationEntry] = performance.getEntriesByType(
      "navigation",
    ) as PerformanceNavigationTiming[];
    const protocol = navigationEntry?.nextHopProtocol ?? "";
    // HTTP/2 and HTTP/3 multiplex requests over a single connection,
    // so the per-host connection limit does not apply
    if (protocol === "h2" || protocol === "h2c" || protocol.startsWith("h3")) {
      return Infinity;
    }
  } catch {
    // Performance API unavailable; fall through to the conservative limit
  }
  return HTTP1_CONCURRENT_CARD_FETCH_LIMIT;
}

const CONCURRENT_CARD_FETCH_LIMIT = getCardsFetchingConcurrencyLimit();

async function runWithConcurrencyLimit(
  tasks: (() => Promise<void>)[],
  limit: number,
): Promise<void> {
  let index = 0;
  async function worker() {
    while (index < tasks.length) {
      const taskIndex = index++;
      await tasks[taskIndex]();
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(limit, tasks.length) }, worker),
  );
}

export const fetchDashboardCardData =
  ({ isRefreshing = false, reload = false, clearCache = false } = {}) =>
  (dispatch: Dispatch, getState: GetState) => {
    const dashboard = getDashboardComplete(getState());
    if (!dashboard) {
      return;
    }

    const selectedTabId = getSelectedTabId(getState());
    const dashboardLoadId = uuid();
    const loadingIds = getLoadingDashCards(getState()).loadingIds;
    const nonVirtualDashcards = getCurrentTabDashboardCards(
      dashboard,
      selectedTabId,
    ).filter(({ dashcard }) => !isVirtualDashCard(dashcard));

    let nonVirtualDashcardsToFetch = [];
    if (isRefreshing) {
      nonVirtualDashcardsToFetch = nonVirtualDashcards.filter(
        ({ dashcard }) => {
          return !loadingIds.includes(dashcard.id);
        },
      );
      const newLoadingIds = nonVirtualDashcardsToFetch.map(({ dashcard }) => {
        return dashcard.id;
      });

      dispatch(
        fetchDashboardCardDataAction({
          currentTime: performance.now(),
          loadingIds: loadingIds.concat(newLoadingIds),
        }),
      );
    } else {
      nonVirtualDashcardsToFetch = nonVirtualDashcards;
      const newLoadingIds = nonVirtualDashcardsToFetch.map(({ dashcard }) => {
        return dashcard.id;
      });

      /**
       * We intentionally do NOT cancel in-flight requests here. Each card's fetch (fetchCardDataAction) handles its
       * own deduplication: it returns early when an identical request is already in-flight and cancels stale requests
       * when parameters change. Batch-cancelling here would abort nearly-complete requests on tab switch, forcing
       * slow queries (e.g. pivots) to restart from scratch. (#70534)
       */

      dispatch(
        fetchDashboardCardDataAction({
          currentTime: performance.now(),
          loadingIds: newLoadingIds,
        }),
      );
    }

    const tasks = nonVirtualDashcardsToFetch.map(
      ({ card, dashcard }) =>
        async () => {
          await dispatch(
            // TODO: fix the return type of getAllDashboardCards to make sure
            // that the relationship between a dashcard and its card
            // is actually reflected in the type system
            fetchCardData(card as Card, dashcard, {
              reload,
              clearCache,
              dashboardLoadId,
            }),
          );
          await dispatch(updateLoadingTitle(nonVirtualDashcardsToFetch.length));
        },
    );

    if (nonVirtualDashcardsToFetch.length > 0) {
      dispatch(
        setDocumentTitle(t`0/${nonVirtualDashcardsToFetch.length} loaded`),
      );

      // TODO: There is a race condition here, when refreshing a dashboard before
      // the previous API calls finished.
      return runWithConcurrencyLimit(tasks, CONCURRENT_CARD_FETCH_LIMIT).then(
        () => {
          dispatch(loadingComplete());
        },
      );
    }
  };

export const reloadDashboardCards =
  () => async (dispatch: Dispatch, getState: GetState) => {
    const dashboard = getDashboardComplete(getState());

    if (!dashboard) {
      return;
    }

    const reloadTasks = getAllDashboardCards(dashboard)
      .filter(({ dashcard }) => !isVirtualDashCard(dashcard))
      .map(({ card, dashcard }) => async () => {
        await dispatch(
          // TODO: fix the return type of getAllDashboardCards to make sure
          // that the relationship between a dashcard and its card
          // is actually reflected in the type system
          fetchCardData(card as Card, dashcard, {
            reload: true,
            ignoreCache: true,
          }),
        );
      });

    await runWithConcurrencyLimit(reloadTasks, CONCURRENT_CARD_FETCH_LIMIT);
  };

export const cancelFetchDashboardCardData = createThunkAction(
  CANCEL_FETCH_DASHBOARD_CARD_DATA,
  () => (dispatch, getState) => {
    const dashboard = getDashboardComplete(getState());

    if (!dashboard) {
      return;
    }

    for (const { card, dashcard } of getAllDashboardCards(dashboard)) {
      dispatch(cancelFetchCardData(card.id, dashcard.id));
    }
  },
);

type InFlightEntry = {
  controller: AbortController;
  queryParams: ReturnType<typeof getDatasetQueryParams>;
};

const cardDataCancelControllers: Record<
  `${DashCardId},${DashboardCard["card_id"]}`,
  InFlightEntry | null
> = {};

function setFetchCardDataCancel(
  card_id: DashboardCard["card_id"],
  dashcard_id: DashCardId,
  controller: AbortController | null,
  queryParams?: ReturnType<typeof getDatasetQueryParams>,
) {
  cardDataCancelControllers[`${dashcard_id},${card_id}`] = controller
    ? { controller, queryParams: queryParams! }
    : null;
}

// machinery to support query cancellation
export const cancelFetchCardData = createAction(
  CANCEL_FETCH_CARD_DATA,
  (card_id, dashcard_id) => {
    const entry = cardDataCancelControllers[`${dashcard_id},${card_id}`];
    if (entry) {
      entry.controller.abort();
      cardDataCancelControllers[`${dashcard_id},${card_id}`] = null;
    }
    return { payload: { dashcard_id, card_id } };
  },
);

export const clearCardData = createAction(
  CLEAR_CARD_DATA,
  (cardId, dashcardId) => ({ payload: { cardId, dashcardId } }),
);

function getDatasetQueryParams(datasetQuery?: JsonQuery) {
  const parameters = datasetQuery?.parameters ?? [];
  return parameters
    .map((parameter) => ({
      ...parameter,
      value: parameter.value ?? null,
    }))
    .sort(sortById);
}

function sortById(a: { id: ParameterId }, b: { id: ParameterId }) {
  return a.id.localeCompare(b.id);
}

// normalizr schemas
const dashcardSchema = new schema.Entity("dashcard");
const dashboardSchema = new schema.Entity("dashboard", {
  dashcards: [dashcardSchema],
});

let fetchDashboardCancellation: AbortController | null;

const EMPTY_LINK_TARGETS: DashboardLinkTargets = {
  questions: {},
  dashboards: {},
};

/**
 * `cards` and `dashboards` on a query-metadata response are not the dashboard's
 * own cards. They are the questions and dashboards its dashcards' click
 * behaviors link to, which the backend collects by walking every dashcard's
 * `click_behavior` (`batch-fetch-dashboard-links` in queries/metadata.clj).
 *
 * Rendering such a link needs its target: a dashboard's `parameters` to build
 * the URL, a question's `Card` to build the query.
 */
function toLinkTargets(
  queryMetadata: DashboardQueryMetadata | undefined,
): DashboardLinkTargets {
  if (queryMetadata == null) {
    return EMPTY_LINK_TARGETS;
  }
  return {
    questions: Object.fromEntries(
      (queryMetadata.cards ?? []).map((card) => [card.id, card]),
    ),
    dashboards: Object.fromEntries(
      (queryMetadata.dashboards ?? []).map((dashboard) => [
        dashboard.id,
        dashboard,
      ]),
    ),
  };
}

export const fetchDashboard = createAsyncThunk(
  "metabase/dashboard/FETCH_DASHBOARD",
  async (
    {
      dashId,
      queryParams,
      options: {
        preserveParameters = false,
        clearCache = true,
        prefetchedDashboard,
      } = {},
    }: {
      dashId: DashboardId;
      queryParams: ParameterValuesMap;
      options?: {
        preserveParameters?: boolean;
        clearCache?: boolean;
        // A fully-hydrated dashboard already in hand (e.g. the response from
        // saving). When provided, skip the GET and use it directly so we don't
        // re-fetch what the server just returned.
        prefetchedDashboard?: Dashboard;
      };
    },
    { getState, dispatch, rejectWithValue },
  ) => {
    if (fetchDashboardCancellation) {
      fetchDashboardCancellation.abort();
    }
    fetchDashboardCancellation = new AbortController();

    try {
      let entities;
      let linkTargets: DashboardLinkTargets = EMPTY_LINK_TARGETS;
      let result;
      const dashboardLoadId = uuid();

      const dashboardType = getDashboardType(dashId);
      const loadedDashboard = getDashboardById(getState(), dashId);

      if (!clearCache && loadedDashboard) {
        entities = {
          dashboard: { [dashId]: loadedDashboard },
          dashcard: Object.fromEntries(
            loadedDashboard.dashcards.map((id) => [
              id,
              getDashCardById(getState(), id),
            ]),
          ),
        };
        result = denormalize(dashId, dashboardSchema, entities);
        // Reusing the loaded dashboard skips the metadata fetch, and its link
        // targets have not changed, so carry them rather than clearing them.
        linkTargets = getLinkTargetEntities(getState());
      } else if (dashboardType === "public") {
        result = await runRtkEndpoint(
          { uuid: dashId, dashboard_load_id: dashboardLoadId },
          dispatch,
          publicApi.endpoints.getPublicDashboard,
          { signal: fetchDashboardCancellation.signal },
        );
        result = {
          ...result,
          id: dashId,
          dashcards: result.dashcards.map((dc: DashboardCard) => ({
            ...dc,
            dashboard_id: dashId,
          })),
        };
      } else if (dashboardType === "embed") {
        result = await runRtkEndpoint(
          { token: dashId, dashboard_load_id: dashboardLoadId },
          dispatch,
          embedApi.endpoints.getEmbedDashboard,
          { signal: fetchDashboardCancellation.signal },
        );
        result = {
          ...result,
          id: dashId,
          dashcards: result.dashcards.map((dc: DashboardCard) => ({
            ...dc,
            dashboard_id: dashId,
          })),
        };
      } else if (dashboardType === "transient") {
        const subPath = String(dashId).split("/").slice(3).join("/");
        const [entity, entityId] = subPath.split(/[/?]/);
        const [response, queryMetadata] = await Promise.all([
          runRtkEndpoint(
            { subPath, dashboard_load_id: dashboardLoadId },
            dispatch,
            automagicDashboardsApi.endpoints.getXrayDashboard,
            { signal: fetchDashboardCancellation.signal },
          ),
          runRtkEndpoint(
            {
              entity,
              entityId,
              dashboard_load_id: dashboardLoadId,
            },
            dispatch,
            automagicDashboardsApi.endpoints.getXrayDashboardQueryMetadata,
            { signal: fetchDashboardCancellation.signal },
          ),
        ]);
        linkTargets = toLinkTargets(queryMetadata);
        result = {
          ...response,
          id: dashId,
          dashcards: response.dashcards.map((dc: DashboardCard) => ({
            ...dc,
            dashboard_id: dashId,
          })),
        };
      } else if (dashboardType === "inline") {
        // HACK: this is horrible but the easiest way to get "inline" dashboards up and running
        // pass the dashboard in as dashboardId, and replace the id with [object Object] because
        // that's what it will be when cast to a string
        // Adding ESLint ignore because this is a hack and we should fix it.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        result = expandInlineDashboard(dashId);
        dashId = result.id = String(dashId);
      } else if (prefetchedDashboard) {
        // The dashboard was just handed to us (e.g. a save response), so skip
        // the GET. We still warm the query metadata cache exactly as the normal
        // path does (served from cache via forceRefetch: false).
        linkTargets = toLinkTargets(
          await runRtkEndpoint(
            { id: dashId, dashboard_load_id: dashboardLoadId },
            dispatch,
            dashboardApi.endpoints.getDashboardQueryMetadata,
            { forceRefetch: false },
          ),
        );
        result = prefetchedDashboard;
      } else {
        const [response, queryMetadata] = await Promise.all([
          runRtkEndpoint(
            { id: dashId, dashboard_load_id: dashboardLoadId },
            dispatch,
            dashboardApi.endpoints.getDashboard,
            { signal: fetchDashboardCancellation.signal },
          ),
          runRtkEndpoint(
            { id: dashId, dashboard_load_id: dashboardLoadId },
            dispatch,
            dashboardApi.endpoints.getDashboardQueryMetadata,
            { forceRefetch: false },
          ),
        ]);
        linkTargets = toLinkTargets(queryMetadata);
        result = response;
      }

      fetchDashboardCancellation = null;

      const isUsingCachedResults = entities != null;
      if (!isUsingCachedResults) {
        // Copy over any virtual cards from the dashcard to the underlying
        // card/question. The result can come straight from RTK Query, whose
        // responses are deeply frozen, so build new objects rather than
        // mutating in place.
        result = {
          ...result,
          dashcards: result.dashcards.map((card: DashboardCard) =>
            card.visualization_settings?.virtual_card
              ? {
                  ...card,
                  card: {
                    ...(card.card ?? {}),
                    ...card.visualization_settings.virtual_card,
                  },
                }
              : card,
          ),
        };
      }

      if (result.param_fields) {
        await dispatch(paramFieldsFetched(result.param_fields));
      }

      const lastUsedParametersValues = result["last_used_param_values"] ?? {};

      const metadata = getMetadata(getState());
      const parameters = getSavedDashboardUiParameters(
        result.dashcards,
        result.parameters,
        result.param_fields,
        metadata,
      );

      const parameterValuesById = preserveParameters
        ? getParameterValues(getState())
        : getParameterValuesByIdFromQueryParams(
            parameters,
            queryParams,
            lastUsedParametersValues,
          );

      entities = entities ?? normalize(result, dashboardSchema).entities;

      return {
        entities,
        linkTargets,
        dashboard: result,
        dashboardId: result.id,
        parameterValues: parameterValuesById,
        preserveParameters,
      };
    } catch (error) {
      if (!isAbortError(error)) {
        console.error(error);
      }
      return rejectWithValue(error);
    }
  },
);
